import { Component, ViewEncapsulation, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AppService } from '@irb/app.service';
import { IrbClientsService } from '@irb/irb-clients/irb-clients.service';
import { IrbClientsEvents, IrbAppEvents } from '@irb-common/events';
import { UtilityService } from '@irb-common/services/utility.service';
import {IAlertDialogData} from "@irb/app.model";

@Component({ standalone: false,
  selector: 'irb-import-cash-settings-dialog',
  templateUrl: './import-cash-settings-dialog.component.html',
  styleUrls: ['./import-cash-settings-dialog.component.scss','../../irb-common/styles/dialog.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ImportCashSettingsDialogComponent implements OnInit {
  file: any;
  hasFile: boolean = false;
  isValidFile: boolean = false;
  invalidFileErrorHeader: string;
  invalidFileErrorMessage: string;
  errorMessage: string;
  progressTimer: any = null;
  isUiTest = false;
  isDistHoldCashEnabled: boolean;
  isCashEqvEnabled: boolean;
  isSetAsideCashEnabled: boolean;

  constructor(
    public dialogRef: MatDialogRef<ImportCashSettingsDialogComponent>,
    public as: AppService,
    public cs: IrbClientsService,
    private util: UtilityService
  ) {
    this.isCashEqvEnabled = this.as.getItem("IRB_APP_CONFIG")?.isCashEqvEnabled || false;
    this.isDistHoldCashEnabled = this.as.getItem("IRB_APP_CONFIG")?.isDistHoldCashEnabled || false;
    this.isSetAsideCashEnabled = this.as.getItem("IRB_APP_CONFIG")?.featureFlags['enable-setaside-cash'] || false;
  }

  ngOnInit() {
  }

  close() {
    this.dialogRef.close();
  }

  onFileChange(event: any) {
    this.file = event.target.files[0];
    this.hasFile = this.isValidFile = true;
    if (!this.file.name.endsWith('.csv')) {
      this.isValidFile = false;
      this.invalidFileErrorHeader = "Invalid file format.";
      this.invalidFileErrorMessage = "Extension type must be \".csv\"";
    }
    if (this.as.maxFileUploadSizeExceeded(this.file)) {
      this.triggerMaxFileUploadSizeExceededError();
    }
  }

  private triggerMaxFileUploadSizeExceededError() {
    this.isValidFile = false;
    this.invalidFileErrorHeader = "Invalid file size.";
    this.invalidFileErrorMessage = "File size exceeds 50MB limit.";
  }

  submitFile() {
    const f = new FormData();
    f.append('ImportAccountCash0', this.file, this.file.name);
    
    // this.cs.importCashSettings(f).subscribe((res: string) => {
    //   this.processAfterValidationStep(res);
    // });
    this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
    this.cs.validateImportCashSettingsAsync(f).subscribe((response: any) => {
      if (this.progressTimer) clearTimeout(this.progressTimer);
      if (response.responseStatus === "SUCCESS" && response.responseUtilityMap)
        this.actionStatusCheckIntervalStart(response.responseUtilityMap.statusId);
    }, () => {
      this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    });
  }

  actionStatusCheckIntervalStart(statusId: any) {
    this.cs.actionStatusCheck(statusId).subscribe((statusResponse: any) => {
      if (statusResponse.responseStatus === "SUCCESS") {
        const responseData = statusResponse.responseData;
        let reqStatus = 'FAILED';
        if (statusResponse.responseUtilityMap && statusResponse.responseUtilityMap.reqStatus) {
          reqStatus = statusResponse.responseUtilityMap.reqStatus.trim().toUpperCase();
        }

        if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
          let timeout = this.isUiTest ? 0 : 1500;
          this.progressTimer = setTimeout(() => {
            this.actionStatusCheckIntervalStart(statusId);
          }, timeout);
        }

        if (reqStatus === 'COMPLETED') {
          this.processAfterValidationStep(responseData);
          if (this.progressTimer) clearTimeout(this.progressTimer);
          this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        }

        if (reqStatus === 'FAILED') {
          this.actionStatusCheckFailed();
        }
      } else {
        this.actionStatusCheckFailed();
      }
    }, () => {
      this.close();
      this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
      if (this.progressTimer) clearTimeout(this.progressTimer);
    });
  }

  actionStatusCheckFailed() {
    this.close();
    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
    if (this.progressTimer) clearTimeout(this.progressTimer);
    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dType: 'ERROR',
      dTitle: '',
      dProceedBtnLabel: '',
      msg: 'An error occurred processing your request.',
      onProceed: () => {
      }
    } as IAlertDialogData);
  }

  processAfterValidationStep(res: any) {
    if (res.includes('Max upload size exceeded')) {
      this.triggerMaxFileUploadSizeExceededError();
    } else {
      const response = JSON.parse(res.substring(res.indexOf('<textarea>') + 10, res.indexOf('</textarea>')));

      if(response.status && response.status === "fail") {
        if(response.message) {
          this.errorMessage = response.message;
        } else {
          this.isValidFile = false;
        }

        return;
      }
      this.close();
      const hasValidRecords = (response.validRecords && response.validRecords.length > 0);
      const hasInvalidRecords = (response.invalidRecords && response.invalidRecords.length > 0);

      if(hasValidRecords && !hasInvalidRecords) {
        this.as.fireEvent(IrbClientsEvents.IMPORT_CASH_SETTINGS_COMPLETE, response);
      } else if(!hasValidRecords && !hasInvalidRecords) {
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
          severity: 'warn',
          summary: 'Import Cash Settings',
          detail: 'The import completed without any cash settings being imported.'
        });
      } else if(hasInvalidRecords) {
        this.as.fireEvent(IrbClientsEvents.SHOW_IMPORT_CASH_SETTINGS_RESULT, response);
      }
    }
  }
}
